import React, { Fragment, useState } from 'react'
import PropTypes from 'prop-types'

import { get, isEmpty } from 'lodash'
import { Box, Button, CircularProgress, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { DismissibleDialog } from '@scoir/modals'

import useIsShadow from 'common/use-is-shadow'
import {
  DEFAULT_AUTOMATED_TRIGGER,
  ONE_TIME_TRIGGER,
} from 'components/SegmentSelector/outreach-message-trigger-constants'
import { validateLink } from 'components/LinkField/MuiLinkField'
import {
  getIsUsingManualStudentSelection,
  getManualStudentSelection,
  getIsUsingMyMatches,
} from 'components/SegmentSelector/segment-service'
import UserTypeSelector from './UserTypeSelector'
import MessageContent from './MessageContent'
import Audience from './Audience'
import AudienceTitle from './AudienceTitle'
import HeaderPill from './HeaderPill'
import Schedule, { SCHEDULED_FIELD_NAME } from './Schedule'
import { hasValidScheduledDate } from 'components/ComposeOutreachMessageButton/services/helpers'
import getModalConfig from './get-modal-config'
import './index.scss'

const { Body, Footer } = DismissibleDialog

const REQUIRED_FIELDS = ['Subject', 'Message', 'Trigger']
const NOT_EMPTY_ARRAYS = [
  { path: 'Segment.Criteria.Must', key: 'CurrentGradYearOffset' },
  {
    path: 'Segment.Criteria.Must',
    key: 'EngagementStage',
    predicate: (message, isOneTimeMessage, isMyMatchesMessage) => isOneTimeMessage && !isMyMatchesMessage,
  },
]

const hasRequiredFields = (message, isOneTimeMessage, isMyMatchesMessage) => {
  for (let i = 0; i < REQUIRED_FIELDS.length; i++) {
    const value = get(message, REQUIRED_FIELDS[i])
    if (isEmpty(value)) {
      return false
    }
  }

  for (let i = 0; i < NOT_EMPTY_ARRAYS.length; i++) {
    const rule = NOT_EMPTY_ARRAYS[i]
    if (!rule.predicate || rule.predicate(message, isOneTimeMessage, isMyMatchesMessage)) {
      const array = get(message, rule.path)
      const value = array && array.find((item) => item.Name === rule.key)
      if (isEmpty(value)) return false
    }
  }

  return true
}

export const MODE_CREATE = 'mode_create'
export const MODE_EDIT = 'mode_edit'
export const MODE_EDIT_DRAFT = 'mode_edit_draft'
export const MODE_COPY = 'mode_copy'

const useStyles = makeStyles((theme) => ({
  spacedFooter: {
    '& > :not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
    alignItems: 'center',
    display: 'flex',
  },
}))

export const ComposeModal = ({
  show,
  mode,
  enforceFocus,
  allowedToSave,
  message,
  onHide,
  onSave,
  isSaving,
  onUpdate,
  onToggleEnforceFocus,
}) => {
  const classes = useStyles()
  const [errors, setErrors] = useState({})
  const [hasValidLink, setHasValidLink] = useState(validateLink(get(message, 'Link', false)))

  const trigger = get(message, 'Trigger', DEFAULT_AUTOMATED_TRIGGER)
  const { saveButtonText, title } = getModalConfig(trigger)
  const isEditing = mode === MODE_EDIT || mode === MODE_EDIT_DRAFT
  const isOneTimeMessage = trigger === ONE_TIME_TRIGGER
  const isScheduled = message?.Status === 'Scheduled'
  const canEditSegment =
    mode === MODE_CREATE ||
    mode === MODE_EDIT_DRAFT ||
    !isOneTimeMessage ||
    (isOneTimeMessage && isScheduled) ||
    mode === MODE_COPY
  const segment = get(message, 'Segment')
  const isUsingManualStudentSelection = getIsUsingManualStudentSelection(segment)
  const isUsingMyMatches = getIsUsingMyMatches(segment)
  const saveAsDraft = () => onSave(false)
  const saveAndPublish = () => onSave(true)
  const headerText = () => {
    switch (mode) {
      case MODE_CREATE:
        return `New ${title}`
      case MODE_EDIT:
      case MODE_EDIT_DRAFT:
        return `Edit ${title}`
      case MODE_COPY:
        return `Copy ${title}`
    }
  }

  const isMissingStudentSelection = isUsingManualStudentSelection && isEmpty(getManualStudentSelection(segment))
  const hasInvalidSegmentation = Object.values(errors).some((value) => Boolean(value))

  let invalidDate = !hasValidScheduledDate(message)
  if (useIsShadow()) {
    // admins shadowing in can change past messages
    invalidDate = false
  }

  const isFormInvalid =
    invalidDate ||
    !hasRequiredFields(message, isOneTimeMessage, isUsingMyMatches) ||
    isMissingStudentSelection ||
    hasInvalidSegmentation

  const saveDisabled = isSaving || !allowedToSave || !hasValidLink || isFormInvalid

  return (
    <DismissibleDialog
      open={show}
      scroll="paper"
      aria-label="View Outreach Message"
      headerText={headerText()}
      onClose={onHide}
      enforceFocus={enforceFocus}
      headerChildren={
        <HeaderPill
          segment={segment}
          canShowCount={isOneTimeMessage && !isUsingManualStudentSelection}
          loadEstimate={!hasInvalidSegmentation}
        />
      }
    >
      <Body
        withBackgroundColor
        multiSection
        className="edit-modal-audience-comp"
      >
        <Paper
          elevation={0}
          component={Box}
          p={3}
          mb={3}
        >
          {canEditSegment && <AudienceTitle trigger={trigger} />}
          {(!isEditing || (isOneTimeMessage && isScheduled)) && (
            <UserTypeSelector
              trigger={trigger}
              segment={segment}
              onUpdate={onUpdate}
            />
          )}
          {canEditSegment && (
            <Audience
              trigger={trigger}
              segment={segment}
              onUpdate={onUpdate}
              onError={(key, value) => {
                if (errors[key] !== value) {
                  setErrors((prev) => {
                    return { ...prev, [key]: value }
                  })
                }
              }}
            />
          )}
          {mode === MODE_EDIT && isOneTimeMessage && !isScheduled && (
            <p className="text-danger">
              Updating this message will change the message in existing student&apos;s inboxes, and will save this
              version to be sent to all students in the future. Only Scoir Admin users can complete this action.
            </p>
          )}
        </Paper>
        <Paper
          elevation={0}
          component={Box}
          p={3}
        >
          <Box pb={3}>
            <Typography variant="h6">Message Details</Typography>
          </Box>
          {isOneTimeMessage && canEditSegment && (
            <Schedule
              date={get(message, SCHEDULED_FIELD_NAME)}
              onUpdate={(date) => onUpdate(SCHEDULED_FIELD_NAME, date)}
            />
          )}
          <MessageContent
            message={message}
            onToggleEnforceFocus={onToggleEnforceFocus}
            onChange={(property, value) => {
              if (property === 'Link') {
                setHasValidLink(validateLink(value))
              }
              onUpdate(property, value)
            }}
          />
        </Paper>
      </Body>
      <Footer>
        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
        >
          <Box>
            <Button
              variant="text"
              color="primary"
              size="large"
              onClick={onHide}
              disabled={isSaving}
              aria-busy={isSaving}
              aria-describedby="progressSaving"
            >
              Cancel
            </Button>
          </Box>
          <Box className={classes.spacedFooter}>
            {isSaving && (
              <CircularProgress
                id="progressSaving"
                aria-label="Loading..."
                size="25px"
                role="status"
              />
            )}
            {(mode === MODE_CREATE || mode === MODE_COPY) && (
              <Fragment>
                <Button
                  id="btnSaveAsDraft"
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={saveAsDraft}
                  disabled={saveDisabled}
                  aria-busy={isSaving}
                  aria-describedby="progressSaving"
                >
                  Save as Draft
                </Button>
                <Button
                  id="btnSave"
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={saveAndPublish}
                  disabled={saveDisabled}
                  aria-busy={isSaving}
                  aria-describedby="progressSaving"
                >
                  {saveButtonText}
                </Button>
              </Fragment>
            )}
            {mode === MODE_EDIT_DRAFT && (
              <Fragment>
                <Button
                  id="btnSaveAsDraft"
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={saveAsDraft}
                  disabled={saveDisabled}
                  aria-busy={isSaving}
                  aria-describedby="progressSaving"
                >
                  Save
                </Button>
                <Button
                  id="btnSave"
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={saveAndPublish}
                  disabled={isFormInvalid || isSaving}
                  aria-busy={isSaving}
                  aria-describedby="progressSaving"
                >
                  {saveButtonText}
                </Button>
              </Fragment>
            )}
            {mode === MODE_EDIT && (
              <Button
                id="btnUpdate"
                variant="contained"
                size="large"
                color={!isOneTimeMessage ? 'primary' : 'default'}
                onClick={saveAndPublish}
                disabled={saveDisabled}
                aria-busy={isSaving}
                aria-describedby="progressSaving"
              >
                Update Message
              </Button>
            )}
          </Box>
        </Box>
      </Footer>
    </DismissibleDialog>
  )
}

ComposeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf([undefined, MODE_CREATE, MODE_EDIT, MODE_EDIT_DRAFT]),
  enforceFocus: PropTypes.bool.isRequired,
  allowedToSave: PropTypes.bool.isRequired,
  message: PropTypes.object,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onToggleEnforceFocus: PropTypes.func.isRequired,
}

ComposeModal.defaultProps = {
  show: false,
  enforceFocus: false,
  allowedToSave: false,
}

export default ComposeModal
